import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { apiCall } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { LANGUAGES, translateFrench } from "./translations";
import { toast } from "sonner";

const LanguageContext = createContext(null);
const allowed = new Set(LANGUAGES.map((item) => item.code));
const LANGUAGE_STORAGE_KEY = "racinefit-language";
const savedLanguage = () => (typeof window !== "undefined" && allowed.has(window.localStorage.getItem(LANGUAGE_STORAGE_KEY)) ? window.localStorage.getItem(LANGUAGE_STORAGE_KEY) : "fr");

export const LanguageProvider = ({ children }) => {
  const { user, updateUser } = useAuth();
  const [language, setLanguage] = useState("fr");

  useEffect(() => {
    setLanguage(allowed.has(user?.language) ? user.language : savedLanguage());
  }, [user?.id, user?.language]);

  useEffect(() => {
    document.documentElement.lang = LANGUAGES.find((item) => item.code === language)?.htmlLang || "fr";
    toast.dismiss();
  }, [language]);

  const setPreferredLanguage = useCallback(async (nextLanguage) => {
    if (!allowed.has(nextLanguage) || nextLanguage === language) return;
    const previous = language;
    setLanguage(nextLanguage);
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, nextLanguage);
    if (!user) return;
    try {
      const result = await apiCall("/auth/language", { method: "PUT", data: { language: nextLanguage } });
      updateUser({ ...user, language: result.language });
    } catch (error) {
      setLanguage(previous);
      window.localStorage.setItem(LANGUAGE_STORAGE_KEY, previous);
      throw error;
    }
  }, [language, updateUser, user]);

  const value = useMemo(() => ({ language, setPreferredLanguage, t: (text) => translateFrench(text, language) }), [language, setPreferredLanguage]);
  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) throw new Error("useLanguage must be used within LanguageProvider");
  return context;
};

export const AutoTranslate = () => {
  const { language } = useLanguage();
  const originals = useRef(new WeakMap());
  const attributeOriginals = useRef(new WeakMap());
  const lastTranslatedValues = useRef(new WeakMap());
  const lastTranslatedAttributes = useRef(new WeakMap());

  useEffect(() => {
    const translateTextNode = (node, isFreshContent = false) => {
      if (!node?.parentElement || node.parentElement.closest("[data-no-translate]")) return;
      const previousTranslation = lastTranslatedValues.current.get(node);
      if (isFreshContent && previousTranslation === node.nodeValue) return;
      if (isFreshContent || previousTranslation !== node.nodeValue) originals.current.set(node, node.nodeValue);
      const base = originals.current.get(node) ?? node.nodeValue;
      originals.current.set(node, base);
      const trimmed = base.trim();
      const translated = translateFrench(trimmed, language);
      const next = base.replace(trimmed, translated);
      if (node.nodeValue !== next) {
        lastTranslatedValues.current.set(node, next);
        node.nodeValue = next;
      }
    };
    const translateAttributes = (element, isFreshContent = false) => {
      if (!(element instanceof Element) || element.closest("[data-no-translate]")) return;
      const saved = attributeOriginals.current.get(element) || {};
      const last = lastTranslatedAttributes.current.get(element) || {};
      ["placeholder", "title", "aria-label"].forEach((name) => {
        if (!element.hasAttribute(name)) return;
        const current = element.getAttribute(name);
        if (isFreshContent && last[name] === current) return;
        if (isFreshContent || last[name] !== current) saved[name] = current;
        const base = saved[name] ?? current;
        saved[name] = base;
        const translated = translateFrench(base, language);
        if (current !== translated) {
          last[name] = translated;
          element.setAttribute(name, translated);
        }
      });
      attributeOriginals.current.set(element, saved);
      lastTranslatedAttributes.current.set(element, last);
    };
    const translateTree = (root) => {
      if (root instanceof Element) {
        translateAttributes(root);
        root.querySelectorAll("[placeholder], [title], [aria-label]").forEach(translateAttributes);
      }
      const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
        acceptNode: (node) => node.parentElement && !["SCRIPT", "STYLE", "TEXTAREA"].includes(node.parentElement.tagName) && !node.parentElement.closest("[data-no-translate]") && node.nodeValue.trim() ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT,
      });
      let node;
      while ((node = walker.nextNode())) translateTextNode(node);
    };
    translateTree(document.body);
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === "characterData") translateTextNode(mutation.target, true);
        if (mutation.type === "attributes") translateAttributes(mutation.target, true);
        mutation.addedNodes.forEach((node) => { if (node.nodeType === Node.ELEMENT_NODE) translateTree(node); else if (node.nodeType === Node.TEXT_NODE) translateTextNode(node, true); });
      });
    });
    observer.observe(document.body, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ["placeholder", "title", "aria-label"] });
    return () => observer.disconnect();
  }, [language]);
  return null;
};